import { useState, type ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  BarChart3,
  Bell,
  CreditCard,
  FileText,
  GraduationCap,
  HeartHandshake,
  History,
  MessageSquareText,
  ClipboardList,
  LibraryBig,
  LayoutDashboard,
  Library,
  type LucideIcon,
  Menu,
  Mic,
  UserRound,
  Users,
  X,
} from 'lucide-react';
import { Wordmark } from '@/components/Wordmark';
import { Avatar } from '@/components/ui/Avatar';
import { Icon } from '@/components/ui/Icon';
import { useCan, useCurrentUser } from '@/features/admin-content/use-current-user';
import { roleLabel } from '@/features/admin-content/staff';
import { cn } from '@/lib/cn';
import { NotificationBell, useUnreadCount } from '@/features/notifications/NotificationBell';
import { Disclaimer, SkipLink } from './shell-parts';

interface StaffNavEntry {
  readonly label: string;
  readonly icon: LucideIcon;
  readonly to:
    | '/admin'
    | '/admin/bank'
    | '/admin/content'
    | '/admin/analytics'
    | '/admin/history'
    | '/admin/library'
    | '/admin/tests'
    | '/admin/lessons'
    | '/admin/feedback'
    | '/admin/my-students'
    | '/admin/sessions'
    | '/admin/students'
    | '/admin/support'
    | '/admin/plans'
    | '/admin/users';
}

function useStaffNav(): readonly StaffNavEntry[] {
  const canContent = useCan('content.admin');
  const canAnalytics = useCan('analytics.read');
  const canPeople = useCan('users.admin');
  const canHistory = useCan('activity.read');
  const canPlans = useCan('billing.admin');
  const { data: me } = useCurrentUser();
  const canTeach = Boolean(me?.staff?.studentSupport);

  const items: StaffNavEntry[] = [{ label: 'Overview', icon: LayoutDashboard, to: '/admin' }];
  if (canContent) {
    items.push({ label: 'Library', icon: LibraryBig, to: '/admin/library' });
    items.push({ label: 'Tests', icon: ClipboardList, to: '/admin/tests' });
    items.push({ label: 'Lessons', icon: GraduationCap, to: '/admin/lessons' });
    items.push({ label: 'Bank', icon: Library, to: '/admin/bank' });
    items.push({ label: 'Content', icon: FileText, to: '/admin/content' });
  }
  if (canAnalytics) items.push({ label: 'Analytics', icon: BarChart3, to: '/admin/analytics' });
  if (canHistory) items.push({ label: 'History', icon: History, to: '/admin/history' });
  if (canPlans) items.push({ label: 'Plans', icon: CreditCard, to: '/admin/plans' });
  if (canTeach) {
    items.push({ label: 'Feedback', icon: MessageSquareText, to: '/admin/feedback' });
    items.push({ label: 'My students', icon: GraduationCap, to: '/admin/my-students' });
    items.push({ label: 'Sessions', icon: Mic, to: '/admin/sessions' });
  }
  if (canPeople) {
    items.push({ label: 'Teacher support', icon: HeartHandshake, to: '/admin/support' });
    items.push({ label: 'People', icon: Users, to: '/admin/users' });
    items.push({ label: 'Students', icon: UserRound, to: '/admin/students' });
  }
  return items;
}

function StaffLink({
  entry,
  onNavigate,
}: {
  entry: StaffNavEntry;
  onNavigate?: (() => void) | undefined;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const active =
    entry.to === '/admin'
      ? pathname === '/admin' || pathname === '/admin/'
      : pathname.startsWith(entry.to);

  return (
    <Link
      to={entry.to}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'focus-ring flex h-10 items-center gap-2.5 rounded-control px-3 text-[15px] transition-colors',
        active
          ? 'bg-primary-tint font-bold text-primary-hover'
          : 'text-ink-2 hover:bg-paper hover:text-ink',
      )}
    >
      <Icon icon={entry.icon} size={18} />
      {entry.label}
    </Link>
  );
}

function StaffSidebar({ onNavigate }: { onNavigate?: (() => void) | undefined }) {
  const nav = useStaffNav();
  const { data: me } = useCurrentUser();
  const roles = (me?.roles ?? []).filter((slug) => slug !== 'student');
  const unread = useUnreadCount();

  return (
    <div className="flex h-full flex-col gap-5 p-4">
      <Wordmark />
      <div className="eyebrow rounded-control bg-primary-tint px-3 py-2 text-primary-hover">
        Staff portal
      </div>

      <nav aria-label="Staff" className="flex flex-1 flex-col gap-0.5 overflow-y-auto">
        {nav.map((entry) => (
          <StaffLink key={entry.label} entry={entry} onNavigate={onNavigate} />
        ))}
      </nav>

      <div className="grid gap-1 border-t border-border pt-3">
        <div className="flex items-center gap-2.5 px-3 pb-1">
          <Avatar name={me?.name.trim() ? me.name : (me?.email ?? 'S')} />
          <span className="min-w-0">
            <span className="block truncate text-small font-bold text-ink">
              {me?.name.trim() ? me.name : (me?.email ?? 'Staff account')}
            </span>
            <span className="block truncate text-overline font-normal text-muted">
              {roles.map(roleLabel).join(', ') || 'Staff'}
            </span>
          </span>
        </div>
        <Link
          to="/admin/notifications"
          onClick={onNavigate}
          className="focus-ring flex h-10 items-center gap-2.5 rounded-control px-3 text-[15px] text-ink-2 hover:bg-paper hover:text-ink"
        >
          <Icon icon={Bell} size={18} />
          Notifications
          {unread > 0 ? (
            <span className="ml-auto rounded-full bg-danger px-1.5 text-[11px] font-bold leading-5 text-white">
              {unread > 9 ? '9+' : unread}
            </span>
          ) : null}
        </Link>
        <Link
          to="/dashboard"
          onClick={onNavigate}
          className="focus-ring flex h-10 items-center gap-2.5 rounded-control px-3 text-[15px] text-ink-2 hover:bg-paper hover:text-ink"
        >
          <Icon icon={UserRound} size={18} />
          Student view
        </Link>
      </div>
    </div>
  );
}

/**
 * Staff portal layout (`/admin/*`): its own sidebar, header and look. It shares no menu with the
 * student portal; the only crossing is the explicit "Student view" link.
 */
export function StaffShell({ children }: { children: ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-dvh bg-paper text-ink">
      <SkipLink />

      <aside className="fixed inset-y-0 left-0 z-30 hidden w-sidebar border-r border-border bg-surface lg:block">
        <StaffSidebar />
      </aside>

      <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-surface px-4 lg:hidden">
        <Wordmark compact />
        <span className="eyebrow flex-1 text-primary-hover">Staff portal</span>
        <NotificationBell to="/admin/notifications" />
        <button
          type="button"
          onClick={() => {
            setMenuOpen(true);
          }}
          aria-label="Open menu"
          aria-expanded={menuOpen}
          className="focus-ring grid size-11 place-items-center rounded-control text-ink"
        >
          <Icon icon={Menu} size={22} />
        </button>
      </header>

      {menuOpen ? (
        <div
          className="fixed inset-0 z-50 bg-ink/50 lg:hidden"
          onClick={(event) => {
            if (event.target === event.currentTarget) setMenuOpen(false);
          }}
        >
          <div className="h-full w-72 max-w-[85vw] overflow-y-auto bg-surface">
            <div className="flex justify-end p-2">
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                }}
                aria-label="Close menu"
                className="focus-ring grid size-11 place-items-center rounded-control text-ink"
              >
                <Icon icon={X} size={22} />
              </button>
            </div>
            <StaffSidebar
              onNavigate={() => {
                setMenuOpen(false);
              }}
            />
          </div>
        </div>
      ) : null}

      <div className="lg:pl-[var(--width-sidebar)]">
        <main id="main-content" className="mx-auto w-full max-w-content px-4 pb-12 pt-6 sm:px-8">
          {children}
        </main>
        <Disclaimer />
      </div>
    </div>
  );
}
